import { Link } from 'react-router-dom';
import { Panel } from '../ui/Panel';
import { useCan } from '../useCan';
import { useAdmin } from '../AdminContext';
import { useCosts } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { basketMarginPercent, boxContribution, marginLines, orderContribution, staffelGiftCost } from '../../core/rules/margin';
import { vatRateOf } from '../../core/rules/revenue';
import { euro, money, toCents } from '../../core/format/currency';
import type { Product } from '../../core/types/catalog';

/**
 * Protocol › Marge: wat er overblijft als iemand het hele protocol bestelt (gratis verzonden, iDEAL),
 * en wat een bijvuldoos met deze producten gemiddeld oplevert. Het staffelcadeau (inkoop van het
 * gekoppelde voorraadartikel) telt mee, net als in de bestelling. Alleen met `finance.margin`.
 */
export function RoutineMarginPanel({
  items
}: {
  items: Product[];
}) {
  const {
    canAll
  } = useCan();
  const {
    site
  } = useAdmin();
  const allowed = canAll('finance.margin');
  const {
    data
  } = useCosts(allowed);
  if (!allowed || items.length === 0) return null;
  return <Panel title="Marge" description="Zonder btw, met je ingeschatte kosten." data-id="element-915" data-mp="1xnxpu2fha0s6">
      {!data ? <p className="text-[13px] text-muted" data-id="element-916" data-mp="2bn5uhaueu13r">Laden…</p> : <RoutineMarginBody items={items} itemCosts={data.itemCosts} costs={data.costs} minBox={site.policy.subscription.minBoxValue} gift={subtotal => staffelGiftCost(site.giftTiers, site.policy.freeShippingFrom, subtotal, data.itemCosts)} data-id="element-917" data-mp="26nt5ajg2ghy0" />}
    </Panel>;
}
type BodyProps = {
  items: Product[];
  itemCosts: Record<string, number>;
  costs: NonNullable<ReturnType<typeof useCosts>['data']>['costs'];
  minBox: number;
  /** Inkoop van de staffelcadeaus bij dit bedrag (euro's). */
  gift: (subtotalEuro: number) => {
    cents: number;
    unknown: number;
  };
};
function RoutineMarginBody({
  items,
  itemCosts,
  costs,
  minBox,
  gift
}: BodyProps) {
  const lines = marginLines(items, itemCosts, vatRateOf);
  const gross = lines.reduce((s, l) => s + l.grossCents, 0);
  const percent = basketMarginPercent(lines);
  // Het staffelcadeau hoort bij dit protocol: zelfde treden als de server bij betalen afboekt.
  const giftCost = gift(gross / 100);
  const first = orderContribution({
    kind: 'protocol',
    lines,
    paymentMethod: 'ideal',
    paidCents: gross,
    shipping: 'pakket-nl',
    giftCostCents: giftCost.cents
  }, costs);
  return <>
      <p className="text-[13px] text-muted" data-id="element-918" data-mp="2hb4g0r4nxbhg">Als iemand het hele protocol bestelt</p>
      <MarginLines breakdown={first} className="mt-2" data-id="element-919" data-mp="2hcz1c6juj5m2" />
      {percent !== null && <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-920" data-mp="2m8xmll9vo7i9">
          Brutomarge {Math.round(percent)}%. Op bijvullen gaat alleen wat bijna op is; een doos op het minimum van {euro(minBox)} laat dan ±{' '}
          <span className="text-ink" data-id="element-921" data-mp="5l2454cc73jx">{money(boxContribution(toCents(minBox), percent, costs))}</span> over.
        </p>}
      {first.missingCost.length > 0 && <Link to="/be/voorraad?tab=inkoop" className="mt-2 inline-block text-[13px] text-ink underline underline-offset-4" data-id="element-922" data-mp="1q5qid5sii5t6">
          Inkoop boeken
        </Link>}
    </>;
}